import type { UnwrapRef } from 'vue'
import { defineStore } from 'pinia'
import { isString } from '@/utils/guards'
import { calendarEndpoints } from '@/utils/endpoints'
import { UTCDate } from '@date-fns/utc'

export const useAutopilotDialogStore = defineStore('autopilotDialog', () => {
  const calendarStore = useCalendarStore()
  const route = useRoute()
  const appStore = useAppStore()

  const promotions = ref<{ name: string }[]>([])

  const isOpen = reactive({
    primary: false,
    secondary: false,
  })

  const nudgeTrigger = ref<'auto' | 'click'>('auto')
  const storage = useStorage<{
    nudged_at: string | null
  }>(`autopilot_${route.query.spot_id}`, {
    nudged_at: null,
  })

  const rules = computed(() => {
    return {
      neverApproved: !isString(
        calendarStore.spotInfo?.approvals.last_fb_approval_time,
      ),
      approving:
        isString(calendarStore.spotInfo?.approvals.last_fb_approval_time) &&
        differenceInCalendarDays(
          new UTCDate(),
          new UTCDate(calendarStore.spotInfo?.approvals.last_fb_approval_time),
        ) <= 60,
      stoppedApproving:
        isString(calendarStore.spotInfo?.approvals.last_fb_approval_time) &&
        differenceInCalendarDays(
          new UTCDate(),
          new UTCDate(calendarStore.spotInfo?.approvals.last_fb_approval_time),
        ) > 60,
    }
  })

  const shouldOpen = () => {
    if (appStore.isAdmin) return false

    if (
      calendarStore.spotInfo?.facebook_account_status !== 'connected' ||
      !calendarStore.spotInfo?.approvals.approve_posts_first // is on auto-pilot
    ) {
      return false
    }

    if (isString(storage.value.nudged_at)) {
      return Object.values({
        neverApproved:
          rules.value.neverApproved &&
          differenceInCalendarDays(
            new UTCDate(),
            new UTCDate(storage.value.nudged_at),
          ) >= 15,
        approving:
          rules.value.approving &&
          differenceInCalendarDays(
            new UTCDate(),
            new UTCDate(storage.value.nudged_at),
          ) >= 60,
        stoppedApproving:
          rules.value.stoppedApproving &&
          differenceInCalendarDays(
            new UTCDate(),
            new UTCDate(storage.value.nudged_at),
          ) >= 15,
      }).some(Boolean)
    }

    return Object.values(rules.value).some(Boolean)
  }

  async function getPromotions() {
    const { data, error } = await useApi(
      `${calendarEndpoints().getCalendarActiveHolidayPromotions(route.query.spot_id!.toString())}`,
    )
      .get()
      .json<UnwrapRef<typeof promotions>>()

    if (!data.value || error.value) {
      console.error('error fetching holiday promotions')
      return
    }

    promotions.value = data.value
  }

  return {
    promotions,
    isOpen,
    shouldOpen,
    rules,
    storage,
    nudgeTrigger,
    getPromotions,
  }
})
